import React, { useState } from "react";
import { isValidRoutingNumber } from "../../../../../lib/RoutingNumber";

// Renders only when PerCompanyConfig's two-part gate holds (role.expenses &&
// the admin's own manage_ach). The vault never returns a routing/account
// number — `ach.routingNumber`/`ach.accountNumber` start empty even for an
// existing record, and a full retype is required to change them (plan §8
// behaviors 2-6). Never persisted outside this form's React state —
// no sessionStorage/localStorage/console.log (plan §8 point 8).
export default function ExpenseAch({
  ach,
  achLoading,
  onChange,
  disabled,
  routingError,
  accountError,
}) {
  const [routingTouched, setRoutingTouched] = useState(false);

  if (achLoading || !ach) {
    return (
      <div className="user-form__field">
        <label className="user-form__label">ACH Information</label>
        <div className="user-form__groups-loading">Loading ACH info...</div>
      </div>
    );
  }

  const routingLocalError =
    routingTouched &&
    ach.routingNumber &&
    !isValidRoutingNumber(ach.routingNumber)
      ? "Not a valid bank routing number."
      : "";
  const routingMessage = routingError || routingLocalError;

  const handleDelete = () => {
    if (
      window.confirm(
        "Delete the stored routing and account number for this user? This also turns off Pay Expenses via ACH."
      )
    ) {
      onChange("enabled", false);
    }
  };

  return (
    <div className="user-form__field">
      <label className="user-form__label">ACH Information</label>

      <label className="user-form__active-toggle">
        <input
          type="checkbox"
          checked={!!ach.enabled}
          onChange={(e) => onChange("enabled", e.target.checked)}
          disabled={disabled}
        />
        <span>Pay Expenses via ACH</span>
      </label>

      {ach.enabled &&
        ach.hasRecord &&
        !ach.routingNumber &&
        !ach.accountNumber && (
          <div className="user-form__ach-on-file">
            <div className="user-form__ach-on-file-row">
              <span className="user-form__ach-on-file-group">
                <span className="user-form__ach-on-file-label">Routing</span>
                <span className="user-form__ach-on-file-value">
                  &bull;&bull;&bull;&bull;&bull;{ach.routingLastFour || "----"}
                </span>
              </span>
              <span className="user-form__ach-on-file-group">
                <span className="user-form__ach-on-file-label">Account</span>
                <span className="user-form__ach-on-file-value">
                  &bull;&bull;&bull;&bull;&bull;{ach.accountLastFour || "----"}
                </span>
              </span>
            </div>
            <div className="user-form__ach-on-file-footer">
              <p className="user-form__ach-on-file-note">
                On file. Retype both fields below to change.
              </p>
              <button
                type="button"
                className="user-form__ach-delete-btn"
                onClick={handleDelete}
                disabled={disabled}
              >
                Delete stored bank info
              </button>
            </div>
          </div>
        )}

      {ach.enabled && (
        <>
          <div className="user-form__ach-number-row">
            <div className="user-form__field user-form__ach-routing-field">
              <label className="user-form__label">Routing Number</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={ach.routingNumber}
                onChange={(e) =>
                  onChange("routingNumber", e.target.value.replace(/\D/g, ""))
                }
                onBlur={() => setRoutingTouched(true)}
                className={`user-form__input${
                  routingMessage ? " input-error" : ""
                }`}
                disabled={disabled}
                maxLength={9}
              />
              {routingMessage && (
                <span className="user-form__error">{routingMessage}</span>
              )}
            </div>

            <div className="user-form__field user-form__ach-account-field">
              <label className="user-form__label">Account Number</label>
              <input
                type="text"
                autoComplete="off"
                value={ach.accountNumber}
                onChange={(e) =>
                  onChange("accountNumber", e.target.value.replace(/\D/g, ""))
                }
                className={`user-form__input${
                  accountError ? " input-error" : ""
                }`}
                disabled={disabled}
                maxLength={17}
              />
              {accountError && (
                <span className="user-form__error">{accountError}</span>
              )}
            </div>
          </div>

          <div className="user-form__field">
            <label className="user-form__label">Account Type</label>
            <select
              value={ach.accountType}
              onChange={(e) => onChange("accountType", e.target.value)}
              className="user-form__input"
              disabled={disabled}
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>

          <label className="user-form__active-toggle">
            <input
              type="checkbox"
              checked={!!ach.prenote}
              onChange={(e) => onChange("prenote", e.target.checked)}
              disabled={disabled}
            />
            <span>Prenote</span>
          </label>
        </>
      )}
    </div>
  );
}
